import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
} from "antd";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { GetActiveAttributesApi } from "../../../api/attributes/GetActiveAttributesApi";
import CreatePlanLevelApi from "../../../api/CreatePlanLevelApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import ColorPicker from "../../sub/ColorPicker";

const { Option } = Select;

function AddPlanLevelModal(props) {
  const [submit, setSubmit] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [color, setColor] = useState("#03a9f4");
  const curLocation = useLocation();

  function handleCancel() {
    setColor("#03a9f4");
    setSubmit(false);
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getAttributes();
      chooseAutoColor();
    }
  }, [props.visible]);

  function chooseAutoColor() {
    let colors = [];
    props.levels?.forEach((level) => {
      colors.push(level?.color);
    });
    let initialColors = [
      "#f44336",
      "#e91e63",
      "#9c27b0",
      "#673ab7",
      "#3f51b5",
      "#2196f3",
      "#03a9f4",
      "#00bcd4",
      "#009688",
      "#4caf50",
      "#8bc34a",
      "#cddc39",
      "#ffeb3b",
      "#ffc107",
      "#ff9800",
      "#ff5722",
      "#795548",
      "#607d8b",
    ];
    let newColors = initialColors.filter(
      (element) => !colors.includes(element)
    );
    setColor(newColors[0]);
  }

  async function getAttributes() {
    const apiResponse = await GetActiveAttributesApi();
    if (apiResponse.success) {
      setAttributes(apiResponse.data);
    }
  }

  async function onFinish(value) {
    setSubmit(true);
    const path = curLocation.pathname;
    const id = path.split("/");

    const apiResponse = await CreatePlanLevelApi(
      value.levelName,
      color,
      value.order,
      value.fixedAttributes?.includes("description"),
      value.fixedAttributes?.includes("tasks"),
      value.fixedAttributes?.includes("kpi"),
      value.fixedAttributes?.includes("target"),
      value.fixedAttributes?.includes("targetDate"),
      value.customAttributes,
      id[4]
    );

    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Level added successfully");
      props.handleOk();
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't add level. Try again later."
      );
    }
    handleCancel();
  }

  function getOrders() {
    let output = [];

    props.levels?.forEach((level) => {
      output.push(
        <Option key={level.order + 1} value={level.order + 1}>
          {level.order + 1}
        </Option>
      );
    });

    return output;
  }

  function renderAttributes() {
    let output = [];
    attributes?.forEach((attribute) => {
      output.push(
        <Col span={8}>
          <Checkbox value={attribute.id}>{attribute.name}</Checkbox>
        </Col>
      );
    });
    return output;
  }

  return (
    <Modal
      title="Add plan level"
      visible={props.visible}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        name="addPlanLevel"
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
        autoComplete="off"
        initialValues={{
          order: props.order || props.levels?.length + 1 || 2,
          color: color,
          attributes: ["target", "targetDate", "description", "kpi"],
        }}
      >
        <Form.Item label="Order*" name="order">
          <Select disabled={props.order === 1} placeholder="Select order">
            {getOrders()}
          </Select>
        </Form.Item>

        <Form.Item
          label="Level name*"
          extra="Give name in singular form"
          name="levelName"
          rules={[
            {
              required: true,
              message: "Please enter level name!",
            },
          ]}
        >
          <Input placeholder="Level name" />
        </Form.Item>

        <Form.Item label="Color*" name="color">
          <div className="w-20 h-8">
            <ColorPicker color={color} onChange={(value) => setColor(value)} />
          </div>
        </Form.Item>

        <Form.Item label="Fixed attributes" name="fixedAttributes">
          <Checkbox.Group style={{ width: "100%" }}>
            <Row>
              <Col span={8}>
                <Checkbox value={"target"}>Target</Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value={"targetDate"}>Target date</Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value={"description"}>Description</Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value={"kpi"}>KPI</Checkbox>
              </Col>
              <Col span={8}>
                <Checkbox value={"tasks"}>Tasks</Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>

        {!!attributes?.length && (
          <Form.Item name="customAttributes">
            <Checkbox.Group style={{ width: "100%" }}>
              <Row>{renderAttributes()}</Row>
            </Checkbox.Group>
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <div className=" flex justify-end">
            <Button className="mr-3 w-28" onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button
              className="btn w-28"
              type="primary"
              htmlType={submit && "submit"}
              disabled={submit}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddPlanLevelModal;
